'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { PlayerStanding } from '@/draw/types';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import type { CelebrationEventData } from '../page';
import type { CelebrationHighlight } from '../celebrationData';
import { ChampionReveal } from './ChampionReveal';
import { StandingsTable } from './StandingsTable';
import { HighlightCards } from './HighlightCards';
import { ShareButton } from './ShareButton';
import { ShareUrlModal } from './ShareUrlModal';
import { PointsChart } from './PointsChart';
import { RadarChartCard } from './RadarChartCard';
import { GamesChart } from './GamesChart';
import { SectionLabel } from './SectionLabel';
import { AnimatedCounter } from './AnimatedCounter';
import { EventCompactHeader } from '../../components/EventCompactHeader';

interface CelebrationClientProps {
  event: CelebrationEventData;
  standings: PlayerStanding[];
  highlights: CelebrationHighlight[];
  focusedPlayer: PlayerStanding | null;
  shareThemeId: number;
  shareUrlBase: string;
  locale: string;
  totalMatches: number;
  totalGames: number;
}

function formatDateRange(
  startDate: string,
  endDate: string,
  locale: string,
  timeZone?: string,
): string {
  // Date-only strings stay UTC (the day must never shift); datetimes use the
  // caller's timeZone ('UTC' for a hydration-safe SSR/first render, undefined
  // for the viewer's local time).
  const tzFor = (s: string) => (s.includes('T') ? timeZone : 'UTC');
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startStr = start.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: tzFor(startDate),
  });
  const endStr = end.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tzFor(endDate),
  });
  return `${startStr}–${endStr}`;
}

const GOLD = '#F5A623';

export function CelebrationClient({
  event,
  standings,
  highlights,
  focusedPlayer,
  shareThemeId: _shareThemeId,
  shareUrlBase,
  locale,
  totalMatches: _totalMatches,
  totalGames: _totalGames,
}: CelebrationClientProps) {
  const t = useTranslations('Celebration');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const isDesktop = useIsDesktop('lg');
  const tz = useHydratedTimeZone();

  const champion = standings[0];
  const heroPlayer =
    focusedPlayer && focusedPlayer.playerId !== champion.playerId ? focusedPlayer : champion;
  const dateRange = formatDateRange(event.startDate, event.endDate, locale, tz);

  const shareButtonProps = {
    playerId: focusedPlayer?.playerId,
    onShareCards: () => setShareModalOpen(true),
  };

  // Stat bar items for hero bottom
  const statItems = isDesktop
    ? [
        { label: t('matchWins'), value: heroPlayer.wins },
        { label: t('gamesWonStat'), value: heroPlayer.gamesWon },
        { label: t('pointsFor'), value: heroPlayer.gamePointsFor },
        {
          label: t('winRate'),
          value:
            heroPlayer.gamePointsFor + heroPlayer.gamePointsAgainst > 0
              ? Math.round(
                  (heroPlayer.gamePointsFor /
                    (heroPlayer.gamePointsFor + heroPlayer.gamePointsAgainst)) *
                    100,
                )
              : 0,
          suffix: '%',
        },
      ]
    : [
        { label: t('wins'), value: heroPlayer.wins },
        { label: t('games'), value: heroPlayer.gamesWon },
        { label: t('points'), value: heroPlayer.points },
      ];

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="border-border bg-background flex items-center gap-3 border-b px-5 py-4 lg:px-10 lg:py-5">
        <EventCompactHeader
          event={{
            name: event.name,
            banner_image: event.bannerImage,
            subtitle: [event.groupName, event.locationName, dateRange].filter(Boolean).join(' · '),
          }}
          className="min-w-0 flex-1"
        />
        <ShareButton {...shareButtonProps} variant="inline" className="hidden shrink-0 lg:block" />
      </div>
      {/* ====== ZONE 1: NAVY HERO ====== */}
      <section className="bg-secondary text-secondary-foreground relative flex min-h-[100dvh] flex-col">
        {/* Diagonal gold slashes — background energy */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute top-0 right-[12%] bottom-0 w-[6px] opacity-[0.15]"
            style={{ background: GOLD, transform: 'skewX(-12deg)' }}
          />
          <div
            className="absolute top-0 right-[16%] bottom-0 w-[2px] opacity-[0.08]"
            style={{ background: GOLD, transform: 'skewX(-12deg)' }}
          />
        </div>

        {/* Giant record watermark */}
        <div
          className="pointer-events-none absolute -right-[3%] -bottom-[8%] text-white/[0.02] select-none"
          style={{ fontFamily: 'var(--font-anton)', fontSize: '50vw', lineHeight: '0.8' }}
        >
          {champion.wins}-{champion.losses}
        </div>

        {/* Champion — vertically centered */}
        <div className="relative z-10 flex flex-1 flex-col justify-center px-5 lg:px-10">
          <ChampionReveal champion={champion} focusedPlayer={focusedPlayer} isDesktop={isDesktop} />
        </div>

        {/* Stat bar at bottom of hero */}
        <div className="relative z-10 flex border-t border-white/[0.08]">
          {statItems.map((s, i) => (
            <div
              key={i}
              className="flex-1 border-r border-white/[0.06] px-4 py-4 last:border-r-0 lg:px-8 lg:py-5"
            >
              <div
                className="text-[28px] leading-none font-black text-white lg:text-[36px]"
                style={{ fontFamily: 'var(--font-anton)' }}
              >
                <AnimatedCounter target={s.value} delay={400 + i * 200} suffix={s.suffix} />
              </div>
              <div className="mt-1 text-[9px] font-bold tracking-[2px] text-white/30 uppercase lg:text-[10px]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ====== GOLD TRANSITION BAR ====== */}
      <div className="h-1.5" style={{ background: GOLD }} />

      {/* ====== ZONE 2: CREAM CONTENT ====== */}
      <div className="bg-cream">
        <div className="mx-auto max-w-3xl px-5 py-10 lg:max-w-[900px] lg:px-10 lg:py-14">
          {/* Highlights */}
          <SectionLabel>{t('eventHighlights')}</SectionLabel>
          <HighlightCards highlights={highlights} />

          {/* Standings */}
          <SectionLabel className="mt-12">{t('finalStandings')}</SectionLabel>
          <StandingsTable standings={standings} highlightPlayerId={focusedPlayer?.playerId} />

          {/* Charts */}
          {standings.length >= 3 && (
            <>
              <SectionLabel className="mt-12">{t('playerComparison')}</SectionLabel>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-[#E8E2D4] bg-white p-5">
                  <PointsChart standings={standings} isDark={false} />
                </div>
                <div className="rounded-2xl border border-[#E8E2D4] bg-white p-5">
                  <RadarChartCard standings={standings} isDark={false} />
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-[#E8E2D4] bg-white p-5 lg:max-w-[calc(50%-0.5rem)]">
                <GamesChart standings={standings} isDark={false} />
              </div>
            </>
          )}

          {/* Footer */}
          <div className="pt-10 pb-20 text-center lg:pb-8">
            <span className="text-[11px] font-extrabold tracking-[3px] text-[#1B2D4F]/[0.15] uppercase">
              PLAYPULSE
            </span>
          </div>
        </div>
      </div>

      {/* Sticky share — mobile */}
      <ShareButton {...shareButtonProps} variant="block" className="lg:hidden" />

      <ShareUrlModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={shareUrlBase}
        accentPrimary={GOLD}
        accentSecondary="#d97706"
        isDark={false}
      />
    </div>
  );
}
